import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import authStore from './authStore';
import routing from './routingStore';
import viewStore from './viewStore';
import Http from '../Http';
import Storage from '../Storage';
import utils, { parseError } from '../utils';
import moment from 'moment';
class OstolaskuStore {
  @observable skeleton;
  @observable currentReceiptId;

  @observable receipts = new Map();
  @observable loadingReceipts = new Map();

  @observable index = new Map();
  @observable loadingIndex = new Map();
  @observable loadingIndexBackground = new Map();

  @observable searchHasMore = true;
  @observable indexHasMore = true;
  @observable indexError = false;

  fetchingMore = false;

  fetchingMoreSearch = false;

  @observable updatingStatus = new Map();

  @observable searchActive = false;
  @observable searchValue = '';
  @observable searching = new Map();
  @observable searchResults = null;
  searchTimeout;

  // last sync times
  receiptsSynced = {};
  indexSynced = {};

  pageContentRef;

  persistCategoryLimits = false;
  scrollTop = 0;

  canGoBack = true;


  constructor() {
    makeObservable(this);
    const receipts = Storage.get('ostolaskut');

    if (receipts) {
      this.receipts.replace(receipts);
    }

    const index = Storage.get('ostolaskuindex');

    if (index) {
      this.index.replace(index);
    }
  }

  @computed
  get hasModule() {
    return authStore.hasModule(13);
  }

  @computed
  get list() {
    return this.index.get(authStore.companyToken);
  }

  @computed
  get cacheId() {
    return authStore.companyToken + '.' + this.currentReceiptId;
  }

  @computed
  get isLoadingIndex() {
    return this.loadingIndex.has(authStore.companyToken);
  }

  @computed
  get isLoadingIndexBackground() {
    return this.loadingIndexBackground.has(authStore.companyToken);
  }

  @computed
  get isSearching() {
    return this.searching.has(authStore.companyToken);
  }

  @computed
  get isUpdatingStatus() {
    return this.updatingStatus.has(this.cacheId);
  }

  @computed
  get isLoading() {
    return this.loadingReceipts.has(this.currentReceiptId);
  }
  // The current receipt in Ostolasku
  @computed
  get receipt() {

    return this.receipts.get(this.cacheId) || this.skeleton || {};
  }

  @computed
  get vat() {
    if (!this.receipt || !this.receipt.vientirivit) return null;

    return this.receipt.vientirivit.reduce((total, next) => {
      return total + next.ALVSUMMA;
    }, 0);
  }

  @computed
  get taxfree() {
    if (typeof this.vat !== 'number') return null;

    return this.receipt.LASKUNSUMMA - this.vat;
  }

  @action.bound
  clear() {
    this.skeleton = null;
    this.currentReceiptId = null;
    this.receiptsSynced = {};
    this.receipts.clear();

  }

  @action.bound
  reset() {
    this.scrollTop = 0;
    this.persistCategoryLimits = false;
    this.searchActive = false;
    this.searchValue = '';
    this.searchResults = null;
    this.indexError = false;

  }

  @action.bound
  async fetchIndex() {
    if (!this.hasModule) {
      return;
    }

    const companyToken = authStore.companyToken;

    if (!this.index.has(companyToken)) {
      this.loadingIndex.set(companyToken, true);
    } else {
      if (this.indexSynced[companyToken] && this.indexSynced[companyToken] > Date.now() - utils.CACHE_TIME) {
        return;
      }

      this.loadingIndexBackground.set(companyToken, true);
    }

    await this.updateIndex();

    runInAction(() => {
      this.loadingIndex.delete(companyToken);
      this.loadingIndexBackground.delete(companyToken);
    });
  }

  @action.bound
  async refreshIndex() {
    const companyToken = authStore.companyToken;

    if (this.loadingIndex.has(companyToken)) {
      return;
    }

    this.loadingIndex.set(companyToken, true);

    await this.updateIndex();

    runInAction(() => {
      this.loadingIndex.delete(companyToken);
    });
  }

  @action.bound
  async updateIndex() {
    const companyToken = authStore.companyToken;

    this.indexError = false;

    try {
      const receipts = await Http.crequest('ostolaskut/grouped');

      runInAction(() => {
        this.index.set(companyToken, receipts);

        this.indexHasMore = receipts.confirmed && receipts.confirmed.length === 20;
      });

      Storage.set('ostolaskuindex', this.index);

      this.indexSynced[companyToken] = Date.now();
    } catch (error) {
      // [parseError] returns a string that will be displayed to the user
      viewStore.showSnackbar(parseError(error));
      runInAction(() => {
        this.indexError = true;
      });
    }
  }

  @action.bound
  async fetchMore() {


    if (this.fetchingMore) {

      return;
    }
    const companyToken = authStore.companyToken;

    this.fetchingMore = true;

    try {
      const confirmed = await Http.crequest('ostolaskut/confirmed?offset=' + this.list.confirmed.length);

      runInAction(() => {
        const receipts = this.list;
        receipts.confirmed = receipts.confirmed.concat(confirmed);

        this.indexHasMore = confirmed.length === 20;

        this.index.set(companyToken, receipts);
      });

      Storage.set('ostolaskuindex', this.index);
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }

    this.fetchingMore = false;

  }

  @action.bound
  async fetch() {
    const receiptId = this.currentReceiptId;

    if (this.receipts.has(this.cacheId) && this.receiptsSynced[receiptId] && this.receiptsSynced[receiptId] > Date.now() - utils.CACHE_TIME) {
      return;
    }

    this.loadingReceipts.set(receiptId, true);

    await this.update();

    runInAction(() => {
      this.loadingReceipts.delete(receiptId);
    });
  }

  @action.bound
  async refresh() {
    const receiptId = this.currentReceiptId;

    if (this.loadingReceipts.has(receiptId)) {
      return;
    }

    this.loadingReceipts.set(receiptId, true);

    await this.update();

    runInAction(() => {
      this.loadingReceipts.delete(receiptId);
    });
  }

  @action.bound
  async update() {

    const receiptId = this.currentReceiptId;
    const cacheId = this.cacheId;

    try {
      const receipt = await Http.crequest('ostolaskut/' + receiptId);

      runInAction(() => {
        this.receipts.set(cacheId, receipt);
      });

      Storage.set('ostolaskut', this.receipts);

      this.receiptsSynced[receiptId] = Date.now();
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }

  }

  @action.bound
  hideReceipt() {
    this.currentReceiptId = null;

    if (this.canGoBack) {
      routing.goBack();
    } else {
      routing.push('/tulos');
    }
  }

  @action.bound
  setCurrent(id, state) {
    if (state) {
      this.skeleton = state.skeleton;
    }

    this.currentReceiptId = id;
    this.fetch();
  }

  @action.bound
  searchChange(e) {
    this.searchValue = e.target.value;

    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.search(this.searchValue.trim());
    }, 500);

    // Resetting values for [fetchMoreSearch]
    this.searchResults = null;
    this.searchHasMore = true;
  }

  @action.bound
  toggleSearch() {
    this.searchActive = !this.searchActive;
  }

  @action.bound
  closeSearch() {
    // this.pageContentRef.scrollTop = 0;
    this.searchActive = false;
    this.searchValue = '';
    this.searchResults = null;
  }
  // Searches Ostolaskut 
  @action.bound
  async search(searchValue) {
    if (!searchValue) {
      return;
    }

    const companyToken = authStore.companyToken;

    this.searching.set(companyToken, true);

    try {

      const response = await Http.crequest('ostolaskut?search=' + this.searchValue);

      this.resetScroll();

      runInAction(() => {

        this.searchResults = response;
      });
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }

    runInAction(() => {
      this.searching.delete(companyToken, false);
    });

  }

  // Fetches more results for search
  // sp is the amount of elements that are being skipped in the server
  @action.bound
  async fetchMoreSearch() {
    // So that [InfiniteScroll] can't call this function multiple times
    if (this.fetchingMoreSearch) {
      return;
    }
    this.fetchingMoreSearch = true;

    try {

      // API will skip the lenght of items that are already in the list when fetching more
      const response = await Http.crequest('ostolaskut?search=' + this.searchValue + '&sp=' + this.searchResults.length);

      runInAction(() => {

        this.searchHasMore = response.length === 20;

        this.searchResults = this.searchResults.concat(response);

      });
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }

    this.fetchingMoreSearch = false;
  }

  @action.bound
  async accept() {
    const cacheId = this.cacheId;
    const receipt = this.receipt;
    const originalStatus = receipt.laskuvaihe;

    let MAKSUEHDOTUSPVM = moment(receipt.MAKSUEHDOTUSPVM);
    // Check if [MAKSUEHDOTUSPVM] is valid date so that moment knows how to [format] it, if not valid set it to null so that it will be overwritten in the database
    if (MAKSUEHDOTUSPVM.isValid()) {
      MAKSUEHDOTUSPVM = moment(receipt.MAKSUEHDOTUSPVM, 'YYYY-MM-DD').format('YYYY-MM-DD');
    } else {
      MAKSUEHDOTUSPVM = null;
    }

    this.updatingStatus.set(cacheId, true);

    // switch category if needed
    this.moveToUnpaid(receipt);

    routing.push('/ostolaskut');

    try {
      const response = await Http.crequest('ostolaskut/' + receipt.ORLASKUID + '/accept', { method: 'post', json: { info: receipt.HUOM, MAKSUEHDOTUSPVM: MAKSUEHDOTUSPVM } });

      runInAction(() => {
        this.receipts.set(cacheId, response);
      });

      this.fetchIndex();

      Storage.set('ostolaskut', this.receipts);
    } catch (error) {
      // restore category if needed
      if (originalStatus === 'KESKEN') {
        this.moveToUnconfirmed(receipt);
      }

      viewStore.showSnackbar('Hyväksyminen epäonnistui');
    }

    runInAction(() => {
      this.updatingStatus.delete(cacheId, false);
    });
  }

  @action.bound
  async reject() {
    const cacheId = this.cacheId;
    const originalStatus = this.receipt.laskuvaihe;
    const receipt = this.receipt;
    let MAKSUEHDOTUSPVM = moment(receipt.MAKSUEHDOTUSPVM);
    // Check if [MAKSUEHDOTUSPVM] is valid date so that moment knows how to [format] it, if not valid set it to null so that it will be overwritten in the database
    if (MAKSUEHDOTUSPVM.isValid()) {
      MAKSUEHDOTUSPVM = moment(receipt.MAKSUEHDOTUSPVM, 'YYYY-MM-DD').format('YYYY-MM-DD');
    } else {
      MAKSUEHDOTUSPVM = null;
    }
    let HUOM;
    if (receipt.HUOM === "") {
      HUOM = null;
    } else {
      HUOM = receipt.HUOM;
    }

    this.updatingStatus.set(cacheId, true);

    // switch category if needed
    this.moveToUnconfirmed(receipt);

    routing.push('/ostolaskut');

    try {
      const response = await Http.crequest('ostolaskut/' + receipt.ORLASKUID + '/reject', { method: 'post', json: { info: HUOM, MAKSUEHDOTUSPVM: MAKSUEHDOTUSPVM } });

      runInAction(() => {
        this.receipts.set(cacheId, response);
      });

      this.fetchIndex();

      Storage.set('ostolaskut', this.receipts);
    } catch (error) {
      // restore category if needed
      if (originalStatus === 'HYVAKSYTTY') {
        this.moveToUnpaid(receipt);
      }

      viewStore.showSnackbar('Hylkäys epäonnistui');
    }

    runInAction(() => {
      this.updatingStatus.delete(cacheId, false);
    });
  }

  @action.bound
  moveToUnconfirmed(receipt) {
    this.moveReceipt(receipt, 'KESKEN');
  }

  @action.bound
  moveToUnpaid(receipt) {
    this.moveReceipt(receipt, 'HYVAKSYTTY');
  }

  @action.bound
  moveReceipt(receipt, toStatus) {
    const list = this.list;

    if (list) {
      const from = toStatus === 'KESKEN' ? list.unpaid : list.unapproved;
      const to = toStatus === 'HYVAKSYTTY' ? list.unpaid : list.unapproved;

      const idx = from.findIndex(ostolasku => ostolasku.ORLASKUID === receipt.ORLASKUID);

      if (idx !== -1) {
        const item = from.splice(idx, 1)[0];
        item.laskuvaihe = toStatus;

        let newIndex = to.findIndex(ostolasku => ostolasku.ERAPVM > receipt.ERAPVM);

        if (newIndex === -1) {
          newIndex = to.length - 1;
        }

        to.splice(newIndex, 0, item);
      }
    }
  }

  @action.bound
  showReceipt(receipt) {
    this.saveScroll();

    this.skeleton = receipt;

    this.canGoBack = true;

    routing.push(`/ostolaskut/${receipt.ORLASKUID}`);
  }

  saveScroll() {
    this.scrollTop = this.pageContentRef.scrollTop;
    this.persistCategoryLimits = true;
  }

  resetScroll() {
    this.scrollTop = 0;
    this.persistCategoryLimits = false;
  }
  // Gets the date from the DatePicker in the [Ostolasku]
  @action.bound
  setMAKSUEHDOTUSPVM(value) {
    runInAction(() => {
      this.receipt.MAKSUEHDOTUSPVM = value;
    });
  }
  // Gets "Huomautukset" from [ConfirmButtons]
  @action.bound

  setHUOM(value) {
    runInAction(() => {
      this.receipt.HUOM = value;
    });

  }
}

export default new OstolaskuStore();
